import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import toast from 'react-hot-toast';
import { fiberAPI } from '../../services/api';
import { MapPin, Route as RouteIcon, X, Trash2, Undo2, Check, Crosshair, Search, Locate, AlertTriangle } from 'lucide-react';
import { PortNode, StatusDot } from './FiberTree';
import { useViewMode } from '../../hooks/useViewMode';

// "Where am I" button for the app view: centers the map on the phone's GPS and drops a dot.
function LocateMe() {
  const map = useMap();
  const [pos, setPos] = useState(null);
  useMapEvents({ locationfound: (e) => setPos(e.latlng), locationerror: () => toast.error('Could not get your location — allow GPS for this site') });
  return (
    <>
      {pos && <CircleMarker center={pos} radius={8} pathOptions={{ color: '#fff', weight: 3, fillColor: '#2563eb', fillOpacity: 1 }} />}
      <button type="button" className="fgm-locate" aria-label="My location"
        onClick={(e) => { e.stopPropagation(); map.locate({ setView: true, maxZoom: 18 }); }}>
        <Locate size={18} />
      </button>
    </>
  );
}

// Ahmedabad — Customer.address.city's own default, so a fresh install with no pins yet
// still centers somewhere sane instead of the middle of the ocean (0,0).
const DEFAULT_CENTER = [23.0225, 72.5714];
const DEFAULT_ZOOM = 13;

// Same SLA window and per-meter cost figures used for the fault/cost read-out below —
// no cost-per-meter figures exist anywhere else in the codebase to reuse.
const SLA_HOURS = 4;
const COST = { feederPerMeter: 35, dropPerMeter: 18, splicingFlat: 250 };

function divIcon(color, label, size = 24) {
  return L.divIcon({
    className: 'fiber-geo-marker',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid var(--cb-ffffff);box-shadow:0 1px 4px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;color:var(--c-ffffff);font-size:${Math.round(size * 0.4)}px;font-weight:800;font-family:sans-serif;">${label || ''}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 2],
  });
}
const OLT_ICON = divIcon('#1565C0', 'OLT', 30);
const SPLITTER_ICON = divIcon('#7c3aed', 'S');
const ONU_ICON_LINKED = divIcon('#16a34a', '', 18);
const ONU_ICON_UNLINKED = divIcon('#f59e0b', '', 18);
// A live-offline ONU gets its own color regardless of customer-link state, so a fault
// stands out on the map at a glance instead of blending into the linked/unlinked colors.
const ONU_ICON_OFFLINE = divIcon('#D64545', '', 18);

// Invisible layer that just listens for map clicks — used for both "place this pin"
// and "add a point to the route I'm tracing" modes.
function ClickCatcher({ onClick }) {
  useMapEvents({ click(e) { onClick(e.latlng); } });
  return null;
}

// Pans/zooms to fit a traced fault path whenever it changes — plain Leaflet imperative
// call, so it has to run inside the map via useMap() rather than as a MapContainer prop.
function FitToSegments({ segments }) {
  const map = useMap();
  useEffect(() => {
    const pts = (segments || []).flat();
    if (pts.length === 0) return;
    map.fitBounds(L.latLngBounds(pts).pad(0.3));
  }, [segments, map]);
  return null;
}

function metersBetween(a, b) {
  return L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng));
}
function fmtINR(n) { return `₹${Math.round(n).toLocaleString('en-IN')}`; }

// The OLT's "last deregister" column is a raw string scraped from its own web UI
// (oltService.js parseONUStatus) with no guaranteed format — best-effort parse, and
// callers must handle a null result (duration genuinely unknown) rather than assume it.
function offlineAgeHours(rawLastDeregister) {
  if (!rawLastDeregister) return null;
  const t = Date.parse(rawLastDeregister);
  return Number.isNaN(t) ? null : (Date.now() - t) / 3600000;
}

function CableCostPopup({ meters, perMeter, label, fieldView }) {
  const material = meters * perMeter;
  const total = material + COST.splicingFlat;
  return (
    <div style={{ minWidth: 190 }}>
      <div className="fgm-pop-title">{label}</div>
      <div className="fgm-pop-row"><span>Length</span><span>{meters.toFixed(0)} m</span></div>
      {!fieldView && (
        <>
          <div className="fgm-pop-row"><span>Cable @ ₹{perMeter}/m</span><span>{fmtINR(material)}</span></div>
          <div className="fgm-pop-row"><span>Splicing (flat)</span><span>{fmtINR(COST.splicingFlat)}</span></div>
          <div className="fgm-pop-row fgm-pop-cost"><span>Estimated total</span><span>{fmtINR(total)}</span></div>
        </>
      )}
    </div>
  );
}

function StatBox({ value, label, tone }) {
  return (
    <div className="fgm-stat">
      <div className={`fgm-stat-num fgm-tone-${tone}`}>{value}</div>
      <div className="fgm-stat-label">{label}</div>
    </div>
  );
}

function FaultsPanel({ faults, onFocus }) {
  if (faults.length === 0) {
    return <div className="fgm-empty">No open faults — every mapped ONU with live OLT status is online.</div>;
  }
  return (
    <div className="fgm-fault-list">
      {faults.map(f => (
        <div key={f.mapping.onuSerial} className={`fgm-fault-item${f.breached ? ' fgm-warn' : ''}`} onClick={() => onFocus(f)}>
          <div className="fgm-fault-top">
            <span className="fgm-fault-id">{f.mapping.customerId?.name || f.mapping.onuSerial}</span>
            <span className={`fgm-sla-badge ${f.breached ? 'fgm-breach' : 'fgm-ok'}`}>{f.slaText}</span>
          </div>
          <div className="fgm-fault-addr">
            {f.mapping.splitterId?.name || 'No splitter assigned'}
            {f.ageHours != null ? ` · offline ${f.ageHours.toFixed(1)}h` : ' · offline duration unknown'}
          </div>
          <div className="fgm-fault-cause">{f.cause}</div>
        </div>
      ))}
    </div>
  );
}

// Injected once — Leaflet popups render their content through a portal Leaflet manages
// itself, so a scoped CSS module wouldn't reach them; a plain global <style> (same trick
// already used below for the responsive layout rule) is what actually applies to popups.
const FGM_STYLES = `
  .fgm-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1px;background:var(--cb-e1e5ea);border:1px solid var(--cb-e1e5ea);border-radius:8px;overflow:hidden;margin-bottom:12px;}
  .fgm-stat{background:var(--cb-ffffff);padding:12px 14px;}
  .fgm-stat-num{font-weight:800;font-size:22px;line-height:1;}
  .fgm-tone-on{color:var(--c-2e9e5b);} .fgm-tone-off{color:var(--c-d64545);} .fgm-tone-neutral{color:var(--c-1565c0);} .fgm-tone-warn{color:var(--c-ffb300);}
  .fgm-stat-label{font-size:11px;color:var(--c-5b6472);margin-top:4px;font-weight:600;}
  .fgm-panel{border:1px solid var(--cb-e1e5ea);border-radius:8px;overflow:hidden;}
  .fgm-panel-title{padding:8px 12px;background:var(--cb-f8f9fa);font-weight:700;font-size:.78rem;border-bottom:1px solid var(--cb-e1e5ea);color:var(--c-1b2430);}
  .fgm-side-tabs{display:flex;border-bottom:1px solid var(--cb-e1e5ea);background:var(--cb-f8f9fa);}
  .fgm-side-tab{flex:1;background:none;border:none;padding:9px 6px;font-weight:700;font-size:.76rem;color:var(--c-5b6472);cursor:pointer;border-bottom:2px solid transparent;}
  .fgm-side-tab.fgm-active{color:var(--c-1565c0);border-bottom-color:var(--cb-1565c0);background:var(--cb-ffffff);}
  .fgm-page-tabs{display:flex;gap:4px;margin-bottom:12px;border-bottom:1px solid var(--cb-e1e5ea);}
  .fgm-page-tab{background:none;border:none;padding:9px 16px;margin-bottom:-1px;font-weight:700;font-size:.82rem;color:var(--c-5b6472);cursor:pointer;border-bottom:2px solid transparent;}
  .fgm-page-tab.fgm-active{color:var(--c-1565c0);border-bottom-color:var(--cb-1565c0);}
  .fgm-structure-full{padding:8px 4px;}
  .fgm-fault-list{max-height:280px;overflow-y:auto;padding:8px;}
  .fgm-empty{padding:14px 12px;font-size:.78rem;color:var(--c-5b6472);}
  .fgm-fault-item{border:1px solid var(--cb-e1e5ea);border-left:3px solid var(--cb-d64545);border-radius:4px;padding:8px 10px;margin-bottom:6px;cursor:pointer;}
  .fgm-fault-item:hover{background:var(--cb-fbf3f3);}
  .fgm-fault-item.fgm-warn{border-left-color:var(--cb-ffb300);}
  .fgm-fault-top{display:flex;justify-content:space-between;gap:8px;align-items:center;flex-wrap:wrap;}
  .fgm-fault-id{font-weight:700;font-size:.78rem;color:var(--c-1b2430);}
  .fgm-fault-addr{font-size:.72rem;color:var(--c-5b6472);margin-top:2px;}
  .fgm-fault-cause{font-size:.7rem;margin-top:4px;color:var(--c-1b2430);}
  .fgm-sla-badge{display:inline-block;padding:1px 7px;border-radius:9px;font-size:10px;font-weight:700;white-space:nowrap;}
  .fgm-sla-badge.fgm-ok{background:var(--cb-e4f5ea);color:var(--c-2e9e5b);}
  .fgm-sla-badge.fgm-breach{background:var(--cb-fbe6e6);color:var(--c-d64545);}
  .fgm-legend-row{display:flex;align-items:center;gap:8px;font-size:.75rem;color:var(--c-5b6472);margin-bottom:6px;}
  .fgm-legend-row:last-child{margin-bottom:0;}
  .fgm-legend-dot{width:11px;height:11px;border-radius:50%;flex-shrink:0;}
  .fgm-legend-line{width:18px;height:0;border-top:3px solid;flex-shrink:0;}
  .fgm-pop-title{font-weight:700;font-size:.85rem;margin-bottom:4px;color:var(--c-1b2430);}
  .fgm-pop-row{display:flex;justify-content:space-between;gap:14px;padding:2px 0;color:var(--c-5b6472);font-size:.8rem;}
  .fgm-pop-row span:last-child{color:var(--c-1b2430);font-weight:600;text-align:right;}
  .fgm-pop-cost{border-top:1px dashed var(--cb-e1e5ea);margin-top:4px;padding-top:4px;font-weight:700;color:var(--c-1b2430);}
  .fgm-pop-status{display:inline-block;padding:1px 8px;border-radius:10px;font-size:.7rem;font-weight:700;}
  .fgm-pop-status.fgm-on{background:var(--cb-e4f5ea);color:var(--c-2e9e5b);}
  .fgm-pop-status.fgm-off{background:var(--cb-fbe6e6);color:var(--c-d64545);}
  .fgm-pop-link{background:none;border:none;font-size:.75rem;font-weight:700;cursor:pointer;padding:0;text-decoration:underline;color:var(--c-8a6d00);}
  .fgm-structure{max-height:340px;overflow-y:auto;padding:8px 4px;}
  /* Compact tree rows matching the map.html reference (thin connector lines, small
     status dots, single-line rows) instead of bordered per-item cards. */
  .ftree-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 8px;border-radius:4px;cursor:pointer;}
  .ftree-row:hover{background:var(--cb-f1f5fb);}
  .ftree-toggle{width:16px;height:16px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:none;border:none;color:var(--c-5b6472);cursor:pointer;padding:0;}
  .ftree-toggle.leaf{visibility:hidden;}
  .ftree-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0;}
  .ftree-label{font-size:.82rem;font-weight:700;color:var(--c-1b2430);}
  .ftree-meta{font-size:.74rem;color:var(--c-5b6472);}
  .ftree-status{font-size:.68rem;font-weight:700;flex-shrink:0;}
  .ftree-status.on{color:var(--c-2e9e5b);}
  .ftree-status.off{color:var(--c-d64545);}
  .ftree-children{margin-left:19px;border-left:1px solid var(--cb-e1e5ea);padding-left:8px;margin-bottom:2px;}
  .ftree-empty{font-size:.76rem;color:var(--c-5b6472);padding:5px 0 5px 27px;}
  .ftree-actions{display:flex;gap:3px;margin-left:auto;}
  .ftree-icon-btn{background:none;border:none;padding:3px;cursor:pointer;color:var(--c-8a93a0);display:flex;align-items:center;border-radius:3px;}
  .ftree-icon-btn:hover{color:var(--c-1565c0);background:var(--cb-e7f0fc);}

  /* Horizontal org-chart tree (PON -> splitters -> ONUs), left to right, boxes joined by
     elbow connectors. A .htree-children column draws one vertical spine (border-left) down
     its own left edge; each .htree-branch draws a short horizontal tick (::before) out to
     that spine at its own vertical center. The spine is trimmed to only span from the first
     branch's center to the last branch's center via ::after overlays matching the page's
     card background (#fff) that paint over the excess above/below. */
  .htree-scroll{overflow-x:auto;padding:10px 12px 16px;}
  .htree-node{display:flex;align-items:center;}
  .htree-box{border:1px solid var(--cb-e1e5ea);border-radius:8px;background:var(--cb-ffffff);padding:8px 10px;min-width:170px;max-width:230px;flex-shrink:0;cursor:pointer;user-select:none;}
  .htree-box:hover{border-color:var(--cb-b9c6d6);}
  .htree-box-top{display:flex;align-items:center;gap:6px;}
  .htree-box-meta{font-size:.7rem;color:var(--c-5b6472);margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .htree-box-cust{font-size:.7rem;color:var(--primary);font-weight:700;margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .htree-box-actions{display:flex;gap:2px;margin-top:5px;padding-top:5px;border-top:1px solid var(--cb-f1f2f4);}
  .htree-children{display:flex;flex-direction:column;margin-left:28px;position:relative;border-left:1px solid var(--cb-c9d2dc);}
  .htree-children::before{content:'';position:absolute;left:-28px;top:50%;width:28px;height:0;border-top:1px solid var(--cb-c9d2dc);}
  .htree-branch{position:relative;padding:5px 0;}
  .htree-branch::before{content:'';position:absolute;left:-28px;top:50%;width:28px;border-top:1px solid var(--cb-c9d2dc);}
  .htree-children > .htree-branch:first-child::after{content:'';position:absolute;left:-1px;top:0;height:50%;width:2px;background:var(--cb-ffffff);}
  .htree-children > .htree-branch:last-child::after{content:'';position:absolute;left:-1px;bottom:0;height:50%;width:2px;background:var(--cb-ffffff);}
  .fgm-locate{position:absolute;right:10px;bottom:24px;z-index:800;width:44px;height:44px;border-radius:50%;border:none;background:var(--cb-ffffff);color:var(--c-1565c0);box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;cursor:pointer;}
  /* App view: one thing at a time. Tabs swap Map / Faults / Unplaced; the map fills the screen. */
  .fgm-app-tabs{display:flex;gap:6px;margin-bottom:10px;overflow-x:auto;}
  .fgm-app-tab{flex:1 0 auto;min-height:40px;padding:0 14px;border:1px solid var(--cb-e1e5ea);border-radius:20px;background:var(--cb-ffffff);color:var(--c-5b6472);font-weight:700;font-size:.82rem;display:flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;cursor:pointer;}
  .fgm-app-tab.fgm-active{background:var(--c-1565c0);border-color:var(--cb-1565c0);color:#fff;}
  .fgm-app-badge{min-width:18px;padding:0 5px;border-radius:9px;background:var(--cb-d64545);color:#fff;font-size:.68rem;line-height:18px;text-align:center;}
  .fgm-app .fiber-geo-panel{padding:0 !important;}
  .fgm-app .fgm-stats{margin-bottom:10px;}
  .fgm-app .fgm-stat{padding:8px 6px;text-align:center;}
  .fgm-app .fgm-stat-num{font-size:18px;}
  .fgm-app .fgm-mapbox{height:calc(100dvh - 330px) !important;min-height:340px;position:relative;}
  .fgm-app[data-apptab="map"] .fgm-sec-faults,.fgm-app[data-apptab="map"] .fgm-sec-place,
  .fgm-app[data-apptab="faults"] .fgm-sec-map,.fgm-app[data-apptab="faults"] .fgm-sec-place,
  .fgm-app[data-apptab="place"] .fgm-sec-map,.fgm-app[data-apptab="place"] .fgm-sec-faults{display:none !important;}
  .fgm-app .fgm-fault-list{max-height:none;}
  .fgm-app .fgm-fault-item{padding:12px;}
  .fgm-app .fgm-fault-id{font-size:.9rem;}
  .fgm-app .fgm-fault-addr,.fgm-app .fgm-fault-cause{font-size:.78rem;}
  .fgm-app .fgm-sec-place .btn{min-height:38px;}
  .fgm-app .fgm-sec-place .form-control{height:40px !important;font-size:16px !important;}
  .fgm-app .fgm-legend-box{display:none;}
  .fgm-app[data-legend="open"] .fgm-legend-box{display:block;}
  @media (max-width: 820px) {
    .fiber-geo-panel { flex-direction: column; }
    .fiber-geo-sidebar { width: 100% !important; }
    .fgm-stats { grid-template-columns: repeat(2, minmax(0,1fr)); }
  }
`;

export default function GeoMapPanel({
  splitters, mappings, liveMap, isAdmin, fieldView, onRefreshSplitters, onRefreshMappings,
  tree, treeLoading, treePon, setTreePon,
  onEditSplitter, onDeleteSplitter, onEditOnu, onDeleteOnu, onAddOnu, onMapOnu, onDeletePort,
}) {
  // Field-tech view is read-only: no drag/place/route-trace and no cable cost figures,
  // same idea as the uploaded map.html reference's Admin/Field-tech toggle.
  const canEdit = isAdmin && !fieldView;

  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);

  const [olt, setOlt] = useState(null);
  const fetchOlt = useCallback(async () => {
    try {
      const { data } = await fiberAPI.getOlt();
      setOlt(data?.data || null);
    } catch { /* OLT may not be configured yet — no pin, no error toast needed */ }
  }, []);
  useEffect(() => { fetchOlt(); }, [fetchOlt]);

  const [pageView, setPageView] = useState('map'); // 'map' | 'structure'
  const { viewMode } = useViewMode();
  const isAppView = viewMode === 'app' || window.innerWidth < 640;
  const [appTab, setAppTab] = useState('map'); // app view only: 'map' | 'faults' | 'place'
  const [legendOpen, setLegendOpen] = useState(false);

  const [mode, setMode] = useState('view'); // 'view' | 'place' | 'trace'
  const [placeTarget, setPlaceTarget] = useState(null); // { kind: 'splitter'|'onu'|'olt', id, label }
  const [tracePoints, setTracePoints] = useState([]);
  const [traceSaveOpen, setTraceSaveOpen] = useState(false);
  const [traceForm, setTraceForm] = useState({ label: '', color: 'var(--c-2563eb)', notes: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  // Manual lat/lng entry — the alternative to click-to-place / drag-on-map, for the OLT,
  // splitters and ONUs alike. Dragging a pin accurately on a phone touchscreen is fiddly,
  // and some locations (e.g. reading coordinates off a GPS unit or another map) are easier
  // to type than to find by panning/zooming. { kind, id, label, lat, lng } — lat/lng are
  // the live text field values (strings, so an in-progress "-72." isn't clobbered by a re-render).
  const [coordModal, setCoordModal] = useState(null);
  const [savingCoord, setSavingCoord] = useState(false);

  // A fault path being highlighted (offline ONU -> its splitter -> parent splitters,
  // for whichever of those legs actually have GPS pins).
  const [traceHighlight, setTraceHighlight] = useState(null); // { serial, segments: [[[lat,lng],[lat,lng]], ...] }

  const fetchRoutes = useCallback(async () => {
    setRoutesLoading(true);
    try {
      const { data } = await fiberAPI.getRoutes();
      setRoutes(data?.data || []);
    } catch { toast.error('Failed to load fiber routes'); }
    finally { setRoutesLoading(false); }
  }, []);
  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const placedSplitters   = useMemo(() => splitters.filter(s => s.coordinates?.lat != null && s.coordinates?.lng != null), [splitters]);
  const unplacedSplitters = useMemo(() => splitters.filter(s => !(s.coordinates?.lat != null && s.coordinates?.lng != null)), [splitters]);
  const placedOnus   = useMemo(() => mappings.filter(m => m.coordinates?.lat != null && m.coordinates?.lng != null), [mappings]);
  const unplacedOnus = useMemo(() => mappings.filter(m => !(m.coordinates?.lat != null && m.coordinates?.lng != null)), [mappings]);

  const splitterById = useMemo(() => {
    const m = {};
    splitters.forEach(s => { m[s._id] = s; });
    return m;
  }, [splitters]);

  const liveFor = useCallback((serial) => (serial ? liveMap?.[serial.toUpperCase()] : null), [liveMap]);

  // ── Live status cross-referenced onto fiber mappings → stats + fault list ──
  const faultData = useMemo(() => {
    const withStatus = mappings
      .map(m => ({ mapping: m, live: liveFor(m.onuSerial) }))
      .filter(x => x.live && x.live.status);
    const onlineCount = withStatus.filter(x => x.live.status === 'online').length;
    const offline = withStatus.filter(x => x.live.status === 'offline');

    const faults = offline.map(x => {
      const ageHours = offlineAgeHours(x.live.lastDeregister);
      const breached = ageHours != null && ageHours > SLA_HOURS;
      const slaText = ageHours == null ? 'Duration unknown' : breached ? 'SLA breached' : `SLA ${(SLA_HOURS - ageHours).toFixed(1)}h left`;
      const splitterId = x.mapping.splitterId?._id;
      const siblingsDown = splitterId ? offline.filter(o => o.mapping.splitterId?._id === splitterId).length : 1;
      const cause = siblingsDown > 1
        ? `Shared with ${siblingsDown - 1} other ONU(s) on this splitter — check splitter/feeder uplink`
        : 'Isolated — likely single drop or NID fault';
      return { mapping: x.mapping, live: x.live, ageHours, breached, slaText, cause };
    }).sort((a, b) => {
      if (a.ageHours == null && b.ageHours == null) return 0;
      if (a.ageHours == null) return 1;
      if (b.ageHours == null) return -1;
      return b.ageHours - a.ageHours; // longest-offline first
    });

    return { onlineCount, faults };
  }, [mappings, liveFor]);

  // ── Cable segments: OLT feeder (OLT -> level-1 splitter), inter-splitter feeder
  // (splitter -> parent splitter), and drop (splitter -> ONU), only for legs where
  // both ends actually have a GPS pin ──
  const feederLines = useMemo(() => {
    const lines = [];
    if (olt?.coordinates?.lat != null) {
      for (const s of splitters) {
        if (s.parentSplitter) continue; // only level-1 splitters hang off the OLT
        if (s.coordinates?.lat != null) {
          lines.push({ key: `olt-feeder-${s._id}`, from: olt.coordinates, to: s.coordinates, label: `Feeder — ${olt.name || 'OLT'} to ${s.name}` });
        }
      }
    }
    for (const s of splitters) {
      const parentId = s.parentSplitter?._id || s.parentSplitter;
      const parent = parentId ? splitterById[parentId] : null;
      if (parent?.coordinates?.lat != null && s.coordinates?.lat != null) {
        lines.push({ key: `feeder-${s._id}`, from: parent.coordinates, to: s.coordinates, label: `Feeder — ${parent.name} to ${s.name}` });
      }
    }
    return lines;
  }, [splitters, splitterById, olt]);

  const dropLines = useMemo(() => {
    const lines = [];
    for (const m of mappings) {
      const splitterId = m.splitterId?._id;
      const s = splitterId ? splitterById[splitterId] : null;
      if (s?.coordinates?.lat != null && m.coordinates?.lat != null) {
        lines.push({ key: `drop-${m._id}`, from: s.coordinates, to: m.coordinates, label: `Drop — ${s.name} to ${m.customerId?.name || m.onuSerial}` });
      }
    }
    return lines;
  }, [mappings, splitterById]);

  // ── Fault trace: walk an offline ONU's splitter chain up to the OLT (or the highest
  // splitter that has a pin, if the OLT itself isn't placed yet), collecting whichever
  // legs have coordinates on both ends. ──
  const traceFault = useCallback((mapping) => {
    const segments = [];
    let current = mapping.splitterId?._id ? splitterById[mapping.splitterId._id] : null;
    if (mapping.coordinates?.lat != null && current?.coordinates?.lat != null) {
      segments.push([[mapping.coordinates.lat, mapping.coordinates.lng], [current.coordinates.lat, current.coordinates.lng]]);
    }
    while (current) {
      const parentId = current.parentSplitter?._id || current.parentSplitter;
      const parent = parentId ? splitterById[parentId] : null;
      if (parent && current.coordinates?.lat != null && parent.coordinates?.lat != null) {
        segments.push([[current.coordinates.lat, current.coordinates.lng], [parent.coordinates.lat, parent.coordinates.lng]]);
      } else if (!parent && !current.parentSplitter && olt?.coordinates?.lat != null && current.coordinates?.lat != null) {
        segments.push([[current.coordinates.lat, current.coordinates.lng], [olt.coordinates.lat, olt.coordinates.lng]]);
      }
      current = parent;
    }
    setTraceHighlight({ serial: mapping.onuSerial, segments });
  }, [splitterById, olt]);

  const focusFault = (f) => { traceFault(f.mapping); setAppTab('map'); };

  const q = search.trim().toLowerCase();
  const filteredUnplacedSplitters = q ? unplacedSplitters.filter(s => s.name.toLowerCase().includes(q)) : unplacedSplitters;
  const filteredUnplacedOnus = q ? unplacedOnus.filter(m =>
    m.onuSerial.toLowerCase().includes(q) || m.customerId?.name?.toLowerCase().includes(q)
  ) : unplacedOnus;

  const startPlacing = (kind, id, label) => {
    if (!canEdit) return;
    setMode('place');
    setAppTab('map');
    setPlaceTarget({ kind, id, label });
  };
  const cancelMode = () => {
    setMode('view');
    setPlaceTarget(null);
    setTracePoints([]);
    setTraceSaveOpen(false);
  };

  const handleMapClick = async (latlng) => {
    if (!canEdit) return;
    if (mode === 'place' && placeTarget) {
      const coordinates = { lat: latlng.lat, lng: latlng.lng };
      try {
        if (placeTarget.kind === 'splitter') {
          await fiberAPI.updateSplitterCoordinates(placeTarget.id, coordinates);
          onRefreshSplitters();
        } else if (placeTarget.kind === 'olt') {
          await fiberAPI.updateOltCoordinates(coordinates);
          fetchOlt();
        } else {
          await fiberAPI.saveMapping(placeTarget.id, { coordinates });
          onRefreshMappings();
        }
        toast.success(`${placeTarget.label} pinned`);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to save pin');
      }
      cancelMode();
    } else if (mode === 'trace') {
      setTracePoints(pts => [...pts, { lat: latlng.lat, lng: latlng.lng }]);
    }
  };

  const openCoordModal = (kind, id, label, coords) => {
    if (!canEdit) return;
    setCoordModal({
      kind, id, label,
      lat: coords?.lat != null ? String(coords.lat) : '',
      lng: coords?.lng != null ? String(coords.lng) : '',
    });
  };

  const saveCoordModal = async () => {
    const lat = parseFloat(coordModal.lat);
    const lng = parseFloat(coordModal.lng);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) { toast.error('Latitude must be a number between -90 and 90'); return; }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) { toast.error('Longitude must be a number between -180 and 180'); return; }
    setSavingCoord(true);
    try {
      if (coordModal.kind === 'splitter') {
        await fiberAPI.updateSplitterCoordinates(coordModal.id, { lat, lng });
        onRefreshSplitters();
      } else if (coordModal.kind === 'olt') {
        await fiberAPI.updateOltCoordinates({ lat, lng });
        fetchOlt();
      } else {
        await fiberAPI.saveMapping(coordModal.id, { coordinates: { lat, lng } });
        onRefreshMappings();
      }
      toast.success(`${coordModal.label} pinned`);
      setCoordModal(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save coordinates');
    } finally {
      setSavingCoord(false);
    }
  };

  const removeSplitterPin = async (s) => {
    try { await fiberAPI.updateSplitterCoordinates(s._id, null); onRefreshSplitters(); toast.success('Pin removed'); }
    catch { toast.error('Failed to remove pin'); }
  };
  const removeOnuPin = async (m) => {
    try { await fiberAPI.saveMapping(m.onuSerial, { coordinates: null }); onRefreshMappings(); toast.success('Pin removed'); }
    catch { toast.error('Failed to remove pin'); }
  };
  const removeOltPin = async () => {
    try { await fiberAPI.updateOltCoordinates(null); fetchOlt(); toast.success('Pin removed'); }
    catch { toast.error('Failed to remove pin'); }
  };

  const dragSplitter = async (s, latlng) => {
    try { await fiberAPI.updateSplitterCoordinates(s._id, { lat: latlng.lat, lng: latlng.lng }); onRefreshSplitters(); }
    catch { toast.error('Failed to move pin'); onRefreshSplitters(); }
  };
  const dragOnu = async (m, latlng) => {
    try { await fiberAPI.saveMapping(m.onuSerial, { coordinates: { lat: latlng.lat, lng: latlng.lng } }); onRefreshMappings(); }
    catch { toast.error('Failed to move pin'); onRefreshMappings(); }
  };
  const dragOlt = async (latlng) => {
    try { await fiberAPI.updateOltCoordinates({ lat: latlng.lat, lng: latlng.lng }); fetchOlt(); }
    catch { toast.error('Failed to move pin'); fetchOlt(); }
  };

  const finishTrace = () => {
    if (tracePoints.length < 2) { toast.error('Add at least 2 points'); return; }
    setTraceSaveOpen(true);
  };
  const saveTrace = async () => {
    setSaving(true);
    try {
      await fiberAPI.createRoute({ label: traceForm.label, color: traceForm.color, notes: traceForm.notes, points: tracePoints });
      toast.success('Route saved');
      fetchRoutes();
      cancelMode();
      setTraceForm({ label: '', color: 'var(--c-2563eb)', notes: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save route');
    } finally { setSaving(false); }
  };

  const deleteRoute = async (id) => {
    if (!window.confirm('Delete this traced route?')) return;
    try { await fiberAPI.deleteRoute(id); setRoutes(rs => rs.filter(r => r._id !== id)); toast.success('Route deleted'); }
    catch { toast.error('Failed to delete route'); }
  };

  // Sensible default view: average of every pin we have, else Ahmedabad.
  const allPins = [
    ...(olt?.coordinates?.lat != null ? [[olt.coordinates.lat, olt.coordinates.lng]] : []),
    ...placedSplitters.map(s => [s.coordinates.lat, s.coordinates.lng]),
    ...placedOnus.map(m => [m.coordinates.lat, m.coordinates.lng]),
  ];
  const center = allPins.length
    ? [allPins.reduce((s, p) => s + p[0], 0) / allPins.length, allPins.reduce((s, p) => s + p[1], 0) / allPins.length]
    : DEFAULT_CENTER;

  return (
    <div className={isAppView ? 'fgm-app' : undefined} data-apptab={appTab} data-legend={legendOpen ? 'open' : 'closed'}>
      <style>{FGM_STYLES}</style>

      {/* ── Stats ── */}
      <div className="fgm-stats">
        <StatBox value={faultData.onlineCount} label="ONUs online" tone="on" />
        <StatBox value={faultData.faults.length} label="ONUs offline" tone="off" />
        <StatBox value={splitters.length} label="Splitters" tone="neutral" />
        <StatBox value={faultData.faults.length} label="Open faults" tone="warn" />
      </div>

      {/* ── Page tabs ── */}
      {isAppView ? (
        <div className="fgm-app-tabs">
          <button className={`fgm-app-tab${pageView === 'map' && appTab === 'map' ? ' fgm-active' : ''}`} onClick={() => { setPageView('map'); setAppTab('map'); }}>Map</button>
          <button className={`fgm-app-tab${pageView === 'map' && appTab === 'faults' ? ' fgm-active' : ''}`} onClick={() => { setPageView('map'); setAppTab('faults'); }}>
            Faults{faultData.faults.length > 0 && <span className="fgm-app-badge">{faultData.faults.length}</span>}
          </button>
          {canEdit && (
            <button className={`fgm-app-tab${pageView === 'map' && appTab === 'place' ? ' fgm-active' : ''}`} onClick={() => { setPageView('map'); setAppTab('place'); }}>
              Unplaced{unplacedSplitters.length + unplacedOnus.length > 0 && <span className="fgm-app-badge" style={{ background: 'var(--cb-f59e0b)' }}>{unplacedSplitters.length + unplacedOnus.length}</span>}
            </button>
          )}
          <button className={`fgm-app-tab${pageView === 'structure' ? ' fgm-active' : ''}`} onClick={() => setPageView('structure')}>Structure</button>
        </div>
      ) : (
        <div className="fgm-page-tabs">
          <button className={`fgm-page-tab${pageView === 'map' ? ' fgm-active' : ''}`} onClick={() => setPageView('map')}>Map</button>
          <button className={`fgm-page-tab${pageView === 'structure' ? ' fgm-active' : ''}`} onClick={() => setPageView('structure')}>Network Structure</button>
        </div>
      )}

      {pageView === 'structure' ? (
        <div className="fgm-panel fgm-structure-full">
          {treeLoading ? (
            <div style={{ padding: '14px 12px', fontSize: '.85rem', color: 'var(--text-muted)' }}>Loading…</div>
          ) : !tree || tree.ports?.length === 0 ? (
            <div className="fgm-empty">
              No fiber topology mapped yet. Add a splitter, then map ONUs to it.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 8px 12px', flexWrap: 'wrap' }}>
                <select className="form-control" style={{ height: 34, padding: '4px 8px', width: 'auto', minWidth: 150, fontSize: '.82rem', lineHeight: 'normal' }}
                  value={treePon} onChange={e => setTreePon(e.target.value)} title="Show a single PON port">
                  <option value="all">All PON Ports</option>
                  {tree.ports.map(p => (
                    <option key={p.ponPort} value={p.ponPort}>PON {p.ponPort}</option>
                  ))}
                </select>
                <span style={{ fontSize: '.76rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                  <StatusDot online={true} />active
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {tree.ports.filter(p => treePon === 'all' || p.ponPort === +treePon).map(p => (
                  <PortNode key={p.ponPort} port={p} liveMap={liveMap}
                    onEditSplitter={onEditSplitter}
                    onDeleteSplitter={onDeleteSplitter}
                    onEditOnu={onEditOnu}
                    onDeleteOnu={onDeleteOnu}
                    onAddOnu={onAddOnu}
                    onMapOnu={onMapOnu}
                    onDeletePort={onDeletePort}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
      <div className="fiber-geo-panel" style={{ display: 'flex', gap: 12, padding: 12 }}>
        {/* ── Map ── */}
        <div className="fgm-sec-map" style={{ flex: 1, minWidth: 0 }}>
          {/* Mode banner (admin, editing) */}
          {canEdit && mode !== 'view' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', marginBottom: 8,
              borderRadius: 8, background: mode === 'trace' ? 'var(--cb-eff6ff)' : 'var(--cb-fefce8)',
              border: `1px solid ${mode === 'trace' ? 'var(--cb-bfdbfe)' : 'var(--cb-fde68a)'}`, fontSize: '.85rem',
            }}>
              <Crosshair size={15} color={mode === 'trace' ? 'var(--c-2563eb)' : 'var(--c-a16207)'} />
              {mode === 'place'
                ? <span>Click on the map to place <strong>{placeTarget?.label}</strong></span>
                : <span>Tracing route — click to add points ({tracePoints.length} so far)</span>}
              <div style={{ flex: 1 }} />
              {mode === 'trace' && tracePoints.length > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={() => setTracePoints(p => p.slice(0, -1))}>
                  <Undo2 size={12} style={{ marginRight: 4 }} />Undo point
                </button>
              )}
              {mode === 'trace' && (
                <button className="btn btn-primary btn-sm" onClick={finishTrace} disabled={tracePoints.length < 2}>
                  <Check size={12} style={{ marginRight: 4 }} />Finish ({tracePoints.length})
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={cancelMode}>
                <X size={12} style={{ marginRight: 4 }} />Cancel
              </button>
            </div>
          )}

          {/* Fault-trace banner */}
          {mode === 'view' && traceHighlight && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', marginBottom: 8,
              borderRadius: 8, background: 'var(--cb-fbe6e6)', border: '1px solid var(--cb-f3b9b9)', fontSize: '.85rem',
            }}>
              <AlertTriangle size={15} color="var(--c-d64545)" />
              <span>Tracing fault path for <strong>{traceHighlight.serial}</strong></span>
              <div style={{ flex: 1 }} />
              <button className="btn btn-secondary btn-sm" onClick={() => setTraceHighlight(null)}>
                <X size={12} style={{ marginRight: 4 }} />Clear
              </button>
            </div>
          )}

          <div className="fgm-mapbox" style={{ height: 560, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <MapContainer center={center} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }}>
              {isAppView && <LocateMe />}
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {canEdit && <ClickCatcher onClick={handleMapClick} />}

              {traceHighlight && <FitToSegments segments={traceHighlight.segments} />}

              {routes.map(r => (
                <Polyline
                  key={r._id}
                  positions={r.points.map(p => [p.lat, p.lng])}
                  pathOptions={{ color: r.color || 'var(--c-2563eb)', weight: 4, opacity: 0.8 }}
                >
                  <Popup>
                    <div style={{ fontSize: '.85rem', minWidth: 140 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{r.label || 'Fiber route'}</div>
                      {r.notes && <div style={{ color: 'var(--c-6b7280)', marginBottom: 6 }}>{r.notes}</div>}
                      {canEdit && (
                        <button className="btn btn-danger btn-sm" onClick={() => deleteRoute(r._id)}>
                          <Trash2 size={11} style={{ marginRight: 4 }} />Delete route
                        </button>
                      )}
                    </div>
                  </Popup>
                </Polyline>
              ))}

              {feederLines.map(l => (
                <Polyline key={l.key} positions={[[l.from.lat, l.from.lng], [l.to.lat, l.to.lng]]} pathOptions={{ color: 'var(--c-8a93a0)', weight: 2, opacity: 0.7 }}>
                  <Popup><CableCostPopup meters={metersBetween(l.from, l.to)} perMeter={COST.feederPerMeter} label={l.label} fieldView={fieldView} /></Popup>
                </Polyline>
              ))}
              {dropLines.map(l => (
                <Polyline key={l.key} positions={[[l.from.lat, l.from.lng], [l.to.lat, l.to.lng]]} pathOptions={{ color: 'var(--c-8a93a0)', weight: 1.6, opacity: 0.7 }}>
                  <Popup><CableCostPopup meters={metersBetween(l.from, l.to)} perMeter={COST.dropPerMeter} label={l.label} fieldView={fieldView} /></Popup>
                </Polyline>
              ))}
              {traceHighlight && traceHighlight.segments.map((seg, i) => (
                <Polyline key={`trace-${i}`} positions={seg} pathOptions={{ color: 'var(--c-ffb300)', weight: 5, opacity: 0.95 }} />
              ))}

              {mode === 'trace' && tracePoints.length > 0 && (
                <>
                  <Polyline positions={tracePoints.map(p => [p.lat, p.lng])} pathOptions={{ color: traceForm.color, weight: 4, dashArray: '6 6' }} />
                  {tracePoints.map((p, i) => (
                    <CircleMarker key={i} center={[p.lat, p.lng]} radius={4} pathOptions={{ color: traceForm.color, fillColor: 'var(--c-ffffff)', fillOpacity: 1 }} />
                  ))}
                </>
              )}

              {olt?.coordinates?.lat != null && (
                <Marker
                  position={[olt.coordinates.lat, olt.coordinates.lng]}
                  icon={OLT_ICON}
                  draggable={canEdit}
                  eventHandlers={canEdit ? { dragend: (e) => dragOlt(e.target.getLatLng()) } : undefined}
                >
                  <Popup>
                    <div style={{ minWidth: 140 }}>
                      <div className="fgm-pop-title">{olt.name || 'OLT'}</div>
                      {canEdit && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openCoordModal('olt', 'olt', olt.name || 'OLT', olt.coordinates)}>
                            <Locate size={11} style={{ marginRight: 4 }} />Edit Lat/Lng
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={removeOltPin}>
                            <X size={11} style={{ marginRight: 4 }} />Remove pin
                          </button>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )}

              {placedSplitters.map(s => (
                <Marker
                  key={s._id}
                  position={[s.coordinates.lat, s.coordinates.lng]}
                  icon={SPLITTER_ICON}
                  draggable={canEdit}
                  eventHandlers={canEdit ? { dragend: (e) => dragSplitter(s, e.target.getLatLng()) } : undefined}
                >
                  <Popup>
                    <div style={{ minWidth: 150 }}>
                      <div className="fgm-pop-title">{s.name}</div>
                      <div className="fgm-pop-row"><span>Splitter</span><span>PON {s.ponPort} · {s.ratio}</span></div>
                      {canEdit && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openCoordModal('splitter', s._id, s.name, s.coordinates)}>
                            <Locate size={11} style={{ marginRight: 4 }} />Edit Lat/Lng
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => removeSplitterPin(s)}>
                            <X size={11} style={{ marginRight: 4 }} />Remove pin
                          </button>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {placedOnus.map(m => {
                const live = liveFor(m.onuSerial);
                const isOffline = live?.status === 'offline';
                const icon = isOffline ? ONU_ICON_OFFLINE : (m.customerId ? ONU_ICON_LINKED : ONU_ICON_UNLINKED);
                return (
                  <Marker
                    key={m._id}
                    position={[m.coordinates.lat, m.coordinates.lng]}
                    icon={icon}
                    draggable={canEdit}
                    eventHandlers={{
                      ...(canEdit ? { dragend: (e) => dragOnu(m, e.target.getLatLng()) } : {}),
                      ...(isOffline ? { click: () => traceFault(m) } : {}),
                    }}
                  >
                    <Popup>
                      <div style={{ minWidth: 150 }}>
                        <div className="fgm-pop-title">{m.customerId?.name || m.onuSerial}</div>
                        <div className="font-mono" style={{ color: 'var(--c-6b7280)', marginBottom: 6, fontSize: '.78rem' }}>{m.onuSerial}</div>
                        {live && (
                          <div className="fgm-pop-row">
                            <span>Status</span>
                            <span className={`fgm-pop-status ${isOffline ? 'fgm-off' : 'fgm-on'}`}>{isOffline ? 'Offline' : 'Online'}</span>
                          </div>
                        )}
                        {isOffline && (
                          <div style={{ marginTop: 6 }}>
                            <button className="fgm-pop-link" onClick={() => traceFault(m)}>Trace fault path</button>
                          </div>
                        )}
                        {canEdit && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => openCoordModal('onu', m.onuSerial, m.customerId?.name || m.onuSerial, m.coordinates)}>
                              <Locate size={11} style={{ marginRight: 4 }} />Edit Lat/Lng
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => removeOnuPin(m)}>
                              <X size={11} style={{ marginRight: 4 }} />Remove pin
                            </button>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>

          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: '.76rem', color: 'var(--text-muted)', alignItems: 'center' }}>
            {canEdit && <span>Drag any pin to reposition it.</span>}
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div className="fiber-geo-sidebar" style={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="fgm-panel fgm-sec-faults">
            <div className="fgm-panel-title">Faults{faultData.faults.length > 0 ? ` (${faultData.faults.length})` : ''}</div>
            <FaultsPanel faults={faultData.faults} onFocus={focusFault} />
          </div>

          {isAppView && (
            <button type="button" className="btn btn-secondary btn-sm fgm-sec-map" onClick={() => setLegendOpen(o => !o)}>
              {legendOpen ? 'Hide legend' : 'Show legend'}
            </button>
          )}
          <div className="fgm-panel fgm-sec-map fgm-legend-box" style={{ padding: '10px 12px' }}>
            <div style={{ fontWeight: 700, fontSize: '.78rem', marginBottom: 8, color: 'var(--c-1b2430)' }}>Legend</div>
            <div className="fgm-legend-row"><span className="fgm-legend-dot" style={{ background: 'var(--cb-1565c0)' }} /> OLT</div>
            <div className="fgm-legend-row"><span className="fgm-legend-dot" style={{ background: 'var(--cb-7c3aed)' }} /> Splitter</div>
            <div className="fgm-legend-row"><span className="fgm-legend-dot" style={{ background: 'var(--cb-16a34a)' }} /> ONU — online, linked to customer</div>
            <div className="fgm-legend-row"><span className="fgm-legend-dot" style={{ background: 'var(--cb-f59e0b)' }} /> ONU — online, unlinked</div>
            <div className="fgm-legend-row"><span className="fgm-legend-dot" style={{ background: 'var(--cb-d64545)' }} /> ONU — offline (fault)</div>
            <div className="fgm-legend-row"><span className="fgm-legend-line" style={{ borderColor: 'var(--cb-8a93a0)' }} /> Cable{fieldView ? '' : ' — click for length & cost'}</div>
            <div className="fgm-legend-row"><span className="fgm-legend-line" style={{ borderColor: 'var(--cb-ffb300)' }} /> Traced fault path</div>
          </div>

          {canEdit && (
            <div className="fgm-sec-place" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={() => { setMode('trace'); setTracePoints([]); setAppTab('map'); }} disabled={mode !== 'view'}>
                <RouteIcon size={13} style={{ marginRight: 5 }} />Trace a Cable Route
              </button>

              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 9, top: 9, color: 'var(--text-muted)' }} />
                <input className="form-control" placeholder="Search unplaced…" value={search} onChange={e => setSearch(e.target.value)}
                  style={{ paddingLeft: 28, height: 32, fontSize: '.8rem' }} />
              </div>

              {olt && !olt.coordinates && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', background: 'var(--bg)', fontWeight: 700, fontSize: '.78rem', borderBottom: '1px solid var(--border)' }}>
                    OLT
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '6px 12px', fontSize: '.8rem' }}>
                    <span style={{ flex: 1 }}>{olt.name || 'OLT'} — not on map yet</span>
                    <button className="btn btn-secondary btn-sm" onClick={() => openCoordModal('olt', 'olt', olt.name || 'OLT', null)} disabled={mode !== 'view'} title="Enter latitude/longitude">
                      <Locate size={11} />
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => startPlacing('olt', 'olt', olt.name || 'OLT')} disabled={mode !== 'view'}>
                      <MapPin size={11} style={{ marginRight: 3 }} />Place
                    </button>
                  </div>
                </div>
              )}

              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', background: 'var(--bg)', fontWeight: 700, fontSize: '.78rem', borderBottom: '1px solid var(--border)' }}>
                  Unplaced Splitters ({filteredUnplacedSplitters.length})
                </div>
                <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                  {filteredUnplacedSplitters.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: '.78rem', color: 'var(--text-muted)' }}>All placed ✓</div>
                  )}
                  {filteredUnplacedSplitters.map(s => (
                    <div key={s._id} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '6px 12px', borderBottom: '1px solid var(--border)', fontSize: '.8rem' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      <button className="btn btn-secondary btn-sm" onClick={() => openCoordModal('splitter', s._id, s.name, null)} disabled={mode !== 'view'} title="Enter latitude/longitude">
                        <Locate size={11} />
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => startPlacing('splitter', s._id, s.name)} disabled={mode !== 'view'}>
                        <MapPin size={11} style={{ marginRight: 3 }} />Place
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', background: 'var(--bg)', fontWeight: 700, fontSize: '.78rem', borderBottom: '1px solid var(--border)' }}>
                  Unplaced ONUs ({filteredUnplacedOnus.length})
                </div>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {filteredUnplacedOnus.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: '.78rem', color: 'var(--text-muted)' }}>All placed ✓</div>
                  )}
                  {filteredUnplacedOnus.map(m => (
                    <div key={m._id} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '6px 12px', borderBottom: '1px solid var(--border)', fontSize: '.8rem' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.customerId?.name || m.onuSerial}
                      </span>
                      <button className="btn btn-secondary btn-sm" onClick={() => openCoordModal('onu', m.onuSerial, m.customerId?.name || m.onuSerial, null)} disabled={mode !== 'view'} title="Enter latitude/longitude">
                        <Locate size={11} />
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => startPlacing('onu', m.onuSerial, m.customerId?.name || m.onuSerial)} disabled={mode !== 'view'}>
                        <MapPin size={11} style={{ marginRight: 3 }} />Place
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', background: 'var(--bg)', fontWeight: 700, fontSize: '.78rem', borderBottom: '1px solid var(--border)' }}>
                  Traced Routes ({routes.length})
                </div>
                <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                  {routesLoading && <div style={{ padding: '10px 12px', fontSize: '.78rem', color: 'var(--text-muted)' }}>Loading…</div>}
                  {!routesLoading && routes.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: '.78rem', color: 'var(--text-muted)' }}>No routes traced yet.</div>
                  )}
                  {routes.map(r => (
                    <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--border)', fontSize: '.8rem' }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label || 'Untitled route'}</span>
                      <button title="Delete" onClick={() => deleteRoute(r._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── Lat/Lng modal — manual coordinate entry for the OLT, a splitter, or an ONU, either
          to place one that's never been pinned or to correct a pin that's already on the map. ── */}
      {coordModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
          onClick={() => setCoordModal(null)}>
          <div style={{ background: 'var(--cb-ffffff)', borderRadius: 10, padding: 22, width: 320, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 2 }}>Set Coordinates</div>
            <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', marginBottom: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {coordModal.label}
            </div>
            <label style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-374151)' }}>Latitude</label>
            <input className="form-control" type="number" step="any" inputMode="decimal"
              style={{ marginTop: 4, marginBottom: 10 }} placeholder="e.g. 23.0225"
              value={coordModal.lat} onChange={e => setCoordModal(m => ({ ...m, lat: e.target.value }))} autoFocus />
            <label style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-374151)' }}>Longitude</label>
            <input className="form-control" type="number" step="any" inputMode="decimal"
              style={{ marginTop: 4, marginBottom: 14 }} placeholder="e.g. 72.5714"
              value={coordModal.lng} onChange={e => setCoordModal(m => ({ ...m, lng: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setCoordModal(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveCoordModal} disabled={savingCoord}>
                {savingCoord ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Save-trace modal ── */}
      {traceSaveOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => setTraceSaveOpen(false)}>
          <div style={{ background: 'var(--cb-ffffff)', borderRadius: 10, padding: 22, width: 340 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 14 }}>Save Cable Route</div>
            <label style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-374151)' }}>Label</label>
            <input className="form-control" style={{ marginTop: 4, marginBottom: 10 }} placeholder="e.g. OLT → Splitter S1"
              value={traceForm.label} onChange={e => setTraceForm(f => ({ ...f, label: e.target.value }))} autoFocus />
            <label style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-374151)' }}>Color</label>
            <input type="color" style={{ marginTop: 4, marginBottom: 10, display: 'block', width: 60, height: 30, padding: 2 }}
              value={traceForm.color} onChange={e => setTraceForm(f => ({ ...f, color: e.target.value }))} />
            <label style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--c-374151)' }}>Notes (optional)</label>
            <textarea className="form-control" rows={2} style={{ marginTop: 4, marginBottom: 14, resize: 'vertical' }}
              value={traceForm.notes} onChange={e => setTraceForm(f => ({ ...f, notes: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setTraceSaveOpen(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveTrace} disabled={saving}>
                {saving ? 'Saving…' : 'Save Route'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
